import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactJson from 'react-json-view';
import moment from 'moment';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';

/**
 * Status icon component based on entry status
 */
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    case 'error':
      return <ExclamationCircleIcon className="w-5 h-5 text-red-500" />;
    case 'timeout':
      return <ClockIcon className="w-5 h-5 text-yellow-500" />;
    default:
      return null;
  }
};

StatusIcon.propTypes = {
  status: PropTypes.oneOf(['success', 'error', 'timeout']).isRequired,
};

/**
 * Collapsible JSON section component
 */
const CollapsibleJson = ({ title, data, defaultCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  if (!data) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800"
      >
        {isCollapsed ? (
          <ChevronRightIcon className="w-4 h-4 mr-1" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 mr-1" />
        )}
        {title}
      </button>
      {!isCollapsed && (
        <div className="mt-1 ml-5 p-2 bg-gray-50 rounded border border-gray-200">
          <ReactJson
            src={data}
            theme="rjv-default"
            collapsed={1}
            displayDataTypes={false}
            enableClipboard
            name={false}
          />
        </div>
      )}
    </div>
  );
};

CollapsibleJson.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object,
  defaultCollapsed: PropTypes.bool,
};

CollapsibleJson.defaultProps = {
  data: null,
  defaultCollapsed: true,
};

/**
 * MessageHistoryEntry - Presentational component for a single history entry
 * Displays timestamp, topic, message type, sent payload, response payload, and status
 */
const MessageHistoryEntry = ({
  timestamp,
  topic,
  messageType,
  topicNumber,
  sentPayload,
  responsePayload,
  status,
  errorMessage,
  onCopyTopic,
}) => {
  const formattedTimestamp = moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
  const relativeTime = moment(timestamp).fromNow();

  const handleCopyTopic = () => {
    if (onCopyTopic) {
      onCopyTopic(topic);
    } else {
      navigator.clipboard.writeText(topic);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm hover:shadow-md transition-shadow">
      {/* Header: Status, Message Type, Timestamp */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <StatusIcon status={status} />
          <span className="font-semibold text-gray-800">
            {topicNumber} - {messageType}
          </span>
        </div>
        <div className="text-sm text-gray-500" title={formattedTimestamp}>
          {formattedTimestamp}
          <span className="ml-2 text-gray-400">({relativeTime})</span>
        </div>
      </div>

      {/* Topic */}
      <div className="mb-2">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Topic
        </label>
        <div className="flex items-center mt-1">
          <TextField
            value={topic}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              readOnly: true,
              style: { fontSize: '0.875rem', backgroundColor: '#f9fafb' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleCopyTopic} title="Copy topic">
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>

      {/* Error Message (if applicable) */}
      {status === 'error' && errorMessage && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Timeout Message */}
      {status === 'timeout' && (
        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
          <strong>Timeout:</strong> No response received from device
        </div>
      )}

      {/* Collapsible Payloads */}
      <CollapsibleJson title="Sent Payload" data={sentPayload} defaultCollapsed />
      <CollapsibleJson
        title="Response Payload"
        data={responsePayload}
        defaultCollapsed={false}
      />
    </div>
  );
};

MessageHistoryEntry.propTypes = {
  timestamp: PropTypes.string.isRequired,
  topic: PropTypes.string.isRequired,
  messageType: PropTypes.string.isRequired,
  topicNumber: PropTypes.number.isRequired,
  sentPayload: PropTypes.object,
  responsePayload: PropTypes.object,
  status: PropTypes.oneOf(['success', 'error', 'timeout']).isRequired,
  errorMessage: PropTypes.string,
  onCopyTopic: PropTypes.func,
};

MessageHistoryEntry.defaultProps = {
  sentPayload: null,
  responsePayload: null,
  errorMessage: null,
  onCopyTopic: null,
};

export default React.memo(MessageHistoryEntry);
